"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect } from "react";
import {
  DefaultValues,
  FieldErrors,
  FieldValues,
  FormState,
  Mode,
  useForm,
  UseFormReturn,
} from "react-hook-form";
import { ZodSchema } from "zod";

import { logger } from "@/core/logging";
import { toast } from "@/core/notifications";
import { debounce } from "@/core/utils";

type UseComplexFormProviderProps<PayloadType extends FieldValues, ResponseType = unknown> = {
  readonly autoSubmit?: boolean;
  /** Stable identifier included in the `form_submit_failed` log so failures are searchable per form. */
  readonly formId?: string;
  readonly defaultValues: DefaultValues<PayloadType>;
  readonly schema: ZodSchema<PayloadType>;
  readonly onSubmit: (data: PayloadType) => Promise<ResponseType>;
  readonly onReset?: () => void;
  readonly onSuccess?: (data: PayloadType, resp: ResponseType) => void;
  readonly onChange?: (data: PayloadType) => void;
  readonly onError?: (err: unknown) => void;
  readonly onValidationError?: (errors: FieldErrors<PayloadType>) => void;
  readonly showValidationErrorToast?: boolean;
  readonly showSuccessToast?: boolean;
  readonly showErrorToast?: boolean;
  readonly successToastMessage?: string;
  readonly validationErrorToastMessage?: string;
  readonly errorToastMessage?: string;
  readonly onChangeDebounce?: number;
  readonly mode?: Mode;
  readonly delayError?: number;
};

type UseComplexFormProviderReturn<PayloadType extends FieldValues> = {
  readonly methods: UseFormReturn<PayloadType>;
  readonly triggerSubmit: () => Promise<void>;
  readonly triggerValidation: () => Promise<void>;
  readonly triggerReset: () => void;
  readonly errors: FieldErrors<PayloadType>;
  readonly isLoading: boolean;
  readonly isValid: boolean;
};

export const useFormProvider = <PayloadType extends FieldValues, ResponseType>({
  autoSubmit = false,
  formId,
  defaultValues,
  schema,
  onSubmit,
  onReset,
  onSuccess,
  onChange,
  onError,
  onValidationError,
  showValidationErrorToast,
  showSuccessToast,
  showErrorToast,
  successToastMessage,
  validationErrorToastMessage,
  errorToastMessage,
  onChangeDebounce = 500,
  mode = "onSubmit",
  delayError,
}: UseComplexFormProviderProps<
  PayloadType,
  ResponseType
>): UseComplexFormProviderReturn<PayloadType> => {
  const methods = useForm<PayloadType>({
    defaultValues,
    mode,
    reValidateMode: "onChange",
    resolver: zodResolver(schema),
    delayError,
  });

  const handleSubmitError = (err: unknown) => {
    logger.error(
      "form_submit_failed",
      { form: formId ?? "unnamed" },
      err instanceof Error ? err : new Error(String(err)),
    );

    onError?.(err);

    if (showErrorToast) {
      toast.error(errorToastMessage ?? "An error occurred");
    }
  };

  const handleSubmitSuccess = (data: PayloadType, resp: ResponseType) => {
    onSuccess?.(data, resp);

    if (showSuccessToast) {
      toast.success(successToastMessage ?? "Success!");
    }
  };

  const handleValidationError = (errors: FieldErrors<PayloadType>) => {
    onValidationError?.(errors);

    if (showValidationErrorToast) {
      toast.warn(validationErrorToastMessage ?? "Validation error");
    }
  };

  const handleSubmit = async (data: PayloadType) => {
    methods.clearErrors("root");

    try {
      const resp = await onSubmit(data);

      handleSubmitSuccess(data, resp);
    } catch (err) {
      handleSubmitError(err);
    }
  };

  const triggerSubmit = methods.handleSubmit(handleSubmit, handleValidationError);

  const handleOnChange = useCallback(
    async (data: Partial<FormState<PayloadType>> & { values: PayloadType }) => {
      onChange?.(data.values);

      if (autoSubmit && Object.keys(data.dirtyFields ?? {}).length > 0) {
        const isValid = await methods.trigger();

        if (isValid) {
          triggerSubmit();
        }
      }
    },
    [autoSubmit, methods, onChange, triggerSubmit],
  );

  const triggerValidation = async () => {
    const isValid = await methods.trigger();

    if (!isValid) {
      handleValidationError(methods.formState.errors);
    }
  };

  const triggerReset = () => {
    methods.reset(defaultValues);
    onReset?.();
  };

  useEffect(() => {
    const callback = methods.subscribe({
      formState: {
        values: true,
        dirtyFields: true,
      },
      callback: debounce(onChangeDebounce, handleOnChange),
    });

    return () => callback();
  }, [handleOnChange, methods, onChangeDebounce]);

  return {
    methods,
    triggerSubmit,
    triggerValidation,
    isValid: methods.formState.isValid,
    triggerReset,
    errors: methods.formState.errors,
    isLoading: methods.formState.isSubmitting,
  };
};
